/**
 * WriterProfile.jsx — public writer profile at /writer/:user_id.
 * Hero (avatar · name · bio · followers · books) + Follow toggle +
 * library grid. Feb 26 2026.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, UserPlus, UserMinus, Loader2, BookOpen, Headphones, Users as UsersIcon, PenTool, Crown, Sparkles, Star, Megaphone, Hash } from 'lucide-react';
import { toast } from 'sonner';
import { authFetch } from '../utils/authUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const WriterProfile = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [following, setFollowing] = useState(false);
  const [toggling, setToggling] = useState(false);

  const fetchProfile = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/writers/${userId}`);
      if (!r.ok) {
        toast.error('Writer not found');
        return;
      }
      const d = await r.json();
      setProfile(d);
      setFollowing(!!d.is_following);
    } finally { setLoading(false); }
  }, [userId]);

  useEffect(() => { if (userId) fetchProfile(); }, [userId, fetchProfile]);

  const toggleFollow = async () => {
    if (toggling) return;
    setToggling(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/writers/${userId}/follow`, { method: 'POST' });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Could not update follow state');
      setFollowing(!!d.following);
      toast.success(d.following ? 'Following' : 'Unfollowed');
      // refresh profile so the follower count updates
      fetchProfile();
    } catch (err) { toast.error(err.message); }
    finally { setToggling(false); }
  };

  if (loading) {
    return (
      <main className="max-w-3xl mx-auto px-4 py-12 text-center text-slate-400">
        <Loader2 className="w-5 h-5 inline-block animate-spin mr-2" /> Loading writer…
      </main>
    );
  }
  if (!profile) {
    return (
      <main className="max-w-3xl mx-auto px-4 py-12 text-center text-slate-500">Writer not found.</main>
    );
  }

  return (
    <main className="max-w-3xl mx-auto px-4 py-6" data-testid="writer-profile-page">
      <header className="flex items-center gap-3 mb-5">
        <button type="button" onClick={() => navigate(-1)} className="p-2 -ml-2 rounded-lg hover:bg-slate-700/50">
          <ArrowLeft className="w-5 h-5 text-slate-200" />
        </button>
        <h1 className="text-xl sm:text-2xl font-bold text-white flex items-center gap-2 truncate flex-1">
          <PenTool className="w-5 h-5 text-purple-400" />
          <span className="truncate">{profile.name}</span>
        </h1>
      </header>

      {/* Hero */}
      <section className="rounded-2xl bg-gradient-to-br from-slate-800/80 via-slate-800/40 to-slate-900/80 border border-slate-700 p-5 sm:p-6 mb-5" data-testid="writer-profile-hero">
        <div className="flex items-start gap-4">
          {profile.picture ? (
            <img src={profile.picture} alt="" className="w-20 h-20 sm:w-24 sm:h-24 rounded-full object-cover ring-2 ring-purple-500/40" />
          ) : (
            <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600" />
          )}
          <div className="flex-1 min-w-0">
            <h2 className="text-lg sm:text-xl font-bold text-white">{profile.name}</h2>
            {profile.bio && <p className="text-sm text-slate-300 mt-1 whitespace-pre-wrap">{profile.bio}</p>}
            {/* Rank + Level pills */}
            <div className="flex flex-wrap items-center gap-2 mt-2">
              <span
                className="inline-flex items-center gap-1 text-[11px] uppercase tracking-widest font-black px-2 py-0.5 rounded-full border border-amber-400/40 bg-amber-500/15 text-amber-200"
                data-testid="writer-profile-rank"
                title="Writer rank"
              >
                <Crown className="w-3 h-3" /> {profile.stats?.writer_rank || 'Ink Novice'}
              </span>
              <span
                className="inline-flex items-center gap-1 text-[11px] uppercase tracking-widest font-black px-2 py-0.5 rounded-full border border-purple-400/40 bg-purple-500/15 text-purple-200"
                data-testid="writer-profile-level"
                title="Scribe level"
              >
                <Sparkles className="w-3 h-3" /> Lvl {profile.stats?.writer_level ?? 1}
              </span>
              {profile.is_ambassador && (
                <span
                  className="inline-flex items-center gap-1 text-[11px] uppercase tracking-widest font-black px-2 py-0.5 rounded-full border border-pink-400/40 bg-pink-500/15 text-pink-200"
                  data-testid="writer-profile-ambassador-badge"
                  title="Smarter-Ambassador"
                >
                  <Megaphone className="w-3 h-3" /> Smarter-Ambassador
                </span>
              )}
              {profile.unique_code && (
                <span
                  className="inline-flex items-center gap-1 text-[11px] uppercase tracking-widest font-mono font-bold px-2 py-0.5 rounded-full border border-sky-400/40 bg-sky-500/15 text-sky-200"
                  data-testid="writer-profile-unique-code"
                  title="Referral code"
                >
                  <Hash className="w-3 h-3" /> {profile.unique_code}
                </span>
              )}
            </div>
            <div className="grid grid-cols-3 gap-3 mt-3">
              <div className="rounded-lg bg-slate-900/40 border border-slate-700 px-3 py-2 text-center">
                <p className="text-[10px] uppercase tracking-widest font-bold text-slate-400">Books</p>
                <p
                  className="text-lg font-black text-amber-300 tabular-nums inline-flex items-center gap-1 justify-center mt-0.5"
                  data-testid="writer-profile-books"
                >
                  <BookOpen className="w-4 h-4" /> {profile.stats?.books_published ?? 0}
                </p>
              </div>
              <div className="rounded-lg bg-slate-900/40 border border-slate-700 px-3 py-2 text-center">
                <p className="text-[10px] uppercase tracking-widest font-bold text-slate-400">Audios</p>
                <p
                  className="text-lg font-black text-fuchsia-300 tabular-nums inline-flex items-center gap-1 justify-center mt-0.5"
                  data-testid="writer-profile-audios"
                >
                  <Headphones className="w-4 h-4" /> {profile.stats?.audios_published ?? 0}
                </p>
              </div>
              <div className="rounded-lg bg-slate-900/40 border border-slate-700 px-3 py-2 text-center">
                <p className="text-[10px] uppercase tracking-widest font-bold text-slate-400">Followers</p>
                <p
                  className="text-lg font-black text-emerald-300 tabular-nums inline-flex items-center gap-1 justify-center mt-0.5"
                  data-testid="writer-profile-followers"
                >
                  <UsersIcon className="w-4 h-4" /> {profile.stats?.followers ?? 0}
                </p>
              </div>
            </div>
            {(profile.stats?.listening_hours || 0) > 0 && (
              <p
                className="text-[12px] text-indigo-200/80 mt-3 text-center"
                data-testid="writer-profile-listening-hours"
              >
                <strong className="text-white">{profile.stats.listening_hours.toLocaleString()}h</strong>{' '}
                of cumulative listening across this author's work
              </p>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={toggleFollow}
          disabled={toggling}
          className={`mt-4 w-full sm:w-auto px-4 py-2 rounded-lg font-semibold transition-colors ${
            following
              ? 'bg-slate-700 hover:bg-slate-600 text-white'
              : 'bg-indigo-600 hover:bg-indigo-700 text-white'
          }`}
          data-testid="writer-profile-follow-btn"
        >
          {toggling ? (
            <Loader2 className="w-4 h-4 mr-2 inline-block animate-spin" />
          ) : following ? (
            <UserMinus className="w-4 h-4 mr-2 inline-block" />
          ) : (
            <UserPlus className="w-4 h-4 mr-2 inline-block" />
          )}
          {following ? 'Following' : 'Follow'}
        </button>
      </section>

      {/* Library grid */}
      <section data-testid="writer-profile-library">
        <h3 className="text-xs uppercase tracking-widest font-bold text-slate-400 mb-3">Library</h3>
        {profile.library?.length === 0 ? (
          <p className="text-sm text-slate-500 italic">No published items yet.</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {profile.library.map((item) => (
              <button
                key={item.ref_id}
                type="button"
                onClick={() => {
                  if (item.is_series) navigate(`/series/${item.ref_id}`);
                  else navigate(`/library?focus=${item.ref_id}`);
                }}
                className="rounded-lg bg-slate-800 border border-slate-700 hover:border-indigo-500/50 overflow-hidden text-left"
              >
                <div className="aspect-[2/3] bg-slate-700 relative">
                  {item.cover_image ? (
                    <img src={item.cover_image} alt={item.title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-slate-500">
                      <BookOpen className="w-8 h-8" />
                    </div>
                  )}
                </div>
                <div className="p-2">
                  <div className="text-xs font-semibold text-white truncate">{item.title}</div>
                  {item.genre && <div className="text-[10px] text-slate-400 truncate">{item.genre}</div>}
                </div>
              </button>
            ))}
          </div>
        )}
      </section>
    </main>
  );
};

export default WriterProfile;
